// pages/VideoCategoriesPage.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useNavigate } from "react-router-dom";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { motion, AnimatePresence } from "framer-motion";
import {
  createVideoCategory,
  deleteVideoCategory,
  fetchVideoCategories,
  updateVideoCategory,
} from "../utils/generalSubjectApi";
import { toast } from "react-toastify";

/* ─── Design Tokens ─────────────────────────────────────────── */
const C = {
  indigo: "#6366f1",
  indigoSoft: "rgba(99,102,241,0.08)",
  indigoMid: "rgba(99,102,241,0.18)",
  violet: "#8b5cf6",
  red: "#ef4444",
  redSoft: "rgba(239,68,68,0.08)",
  ink: "#0f172a",
  slate: "#64748b",
  muted: "#94a3b8",
  border: "rgba(15,23,42,0.08)",
  surface: "rgba(255,255,255,0.80)",
  bg: "#f8faff",
};

const glass = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: C.surface,
  border: `1px solid ${C.border}`,
  boxShadow:
    "0 4px 24px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
};

/* ─── Category Row Card ─────────────────────────────────────── */
const CategoryCard = ({ cat, index, onEdit, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, x: -20, scale: 0.96 }}
    transition={{
      duration: 0.35,
      delay: index * 0.04,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    <Box
      sx={{
        ...glass,
        borderRadius: "18px",
        px: 3,
        py: 2.2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "box-shadow 0.25s, transform 0.25s",
        "&:hover": {
          boxShadow:
            "0 12px 36px rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.95)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            background: C.indigoSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FolderRoundedIcon sx={{ color: C.indigo, fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            fontSize: "0.95rem",
            color: C.ink,
            letterSpacing: "-0.01em",
          }}
        >
          {cat.title}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.5}>
        <motion.div whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.92 }}>
          <IconButton
            onClick={() => onEdit(cat)}
            size="small"
            sx={{
              color: C.indigo,
              borderRadius: "10px",
              width: 34,
              height: 34,
              "&:hover": { background: C.indigoSoft },
            }}
          >
            <EditRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </motion.div>
        <motion.div whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.92 }}>
          <IconButton
            onClick={() => onDelete(cat)}
            size="small"
            sx={{
              color: C.red,
              borderRadius: "10px",
              width: 34,
              height: 34,
              "&:hover": { background: C.redSoft },
            }}
          >
            <DeleteRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </motion.div>
      </Stack>
    </Box>
  </motion.div>
);

/* ─── Styled Dialog ─────────────────────────────────────────── */
const PremiumDialog = ({ open, onClose, title, children, actions }) => (
  <AnimatePresence>
    {open && (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, scale: 0.94, y: 20 },
          animate: { opacity: 1, scale: 1, y: 0 },
          exit: { opacity: 0, scale: 0.94, y: 20 },
          transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
          sx: {
            ...glass,
            borderRadius: "28px",
            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(8px)",
              background: "rgba(15,23,42,0.18)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "1.25rem",
            color: C.ink,
            px: 4,
            pt: 4,
            pb: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {title}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: C.muted, borderRadius: "10px" }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 0, pt: 2.5 }}>{children}</DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 2.5, gap: 1 }}>
          {actions}
        </DialogActions>
      </Dialog>
    )}
  </AnimatePresence>
);

/* ─── Page ──────────────────────────────────────────────────── */
export default function VideoCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchVideoCategories();
      setCategories(res || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = () => {
    setName("");
    setEditingId(null);
    setOpen(true);
  };
  const handleEdit = (cat) => {
    setName(cat.title);
    setEditingId(cat.id);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await updateVideoCategory(editingId, { title: name });
        toast.success(`${name} category updated successfully`); // Edit message
      } else {
        await createVideoCategory({ title: name });
        toast.success(`${name} category created successfully`); // Create message
      }

      setOpen(false);
      setName("");
      setEditingId(null);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong"); // Good practice to add this!
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteVideoCategory(deleteTarget.id);
      setDeleteTarget(null);
      fetchData();
      toast.info(`${name} category deleted succesfully`);
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse 900px 600px at 80% -10%, rgba(99,102,241,0.07) 0%, transparent 60%),
                     radial-gradient(ellipse 600px 500px at -5% 90%, rgba(139,92,246,0.06) 0%, transparent 60%),
                     ${C.bg}`,
        position: "relative",
      }}
    >
      <Box
        sx={{
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 8 },
          pb: 12,
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
                    <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 4,
              px: 2.5,
              py: 1.2,
              borderRadius: "14px",
              cursor: "pointer",
              ...glass,
              fontWeight: 600,
              color: "#475569",
              fontSize: "0.88rem",
              "&:hover": { color: "#6366f1" },
              transition: "color 0.2s",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} /> Back
          </Box>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            mb={6}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: C.ink,
                  letterSpacing: "-0.04em",
                  lineHeight: 1.05,
                }}
              >
                Video Categories
              </Typography>
              <Typography
                sx={{
                  color: C.slate,
                  fontSize: "0.88rem",
                  mt: 0.8,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading
                  ? "Loading…"
                  : `${categories.length} categor${
                      categories.length !== 1 ? "ies" : "y"
                    }`}
              </Typography>
            </Box>

            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleCreate}
                sx={{
                  px: 3.5,
                  py: 1.4,
                  borderRadius: "14px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  letterSpacing: "-0.01em",
                  background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                  boxShadow: "0 8px 24px rgba(99,102,241,0.34)",
                  textTransform: "none",
                  "&:hover": {
                    boxShadow: "0 14px 36px rgba(99,102,241,0.44)",
                    background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                  },
                  transition: "box-shadow 0.25s",
                }}
              >
                New Category
              </Button>
            </motion.div>
          </Stack>
        </motion.div>

        {/* Divider accent */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "left" }}
        >
          <Box
            sx={{
              height: 2,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${C.indigo}, ${C.violet}, transparent)`,
              mb: 4,
              opacity: 0.35,
            }}
          />
        </motion.div>

        {/* List */}
        <Stack spacing={2}>
          <AnimatePresence mode="popLayout">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Box
                    sx={{
                      ...glass,
                      borderRadius: "18px",
                      px: 3,
                      py: 2.2,
                      height: 64,
                      background: "rgba(255,255,255,0.5)",
                    }}
                  >
                    <Box
                      sx={{
                        height: 14,
                        width: `${40 + i * 12}%`,
                        borderRadius: 2,
                        background:
                          "linear-gradient(90deg, rgba(15,23,42,0.06) 25%, rgba(15,23,42,0.1) 50%, rgba(15,23,42,0.06) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.6s infinite",
                        "@keyframes shimmer": {
                          "0%": { backgroundPosition: "200% 0" },
                          "100%": { backgroundPosition: "-200% 0" },
                        },
                      }}
                    />
                  </Box>
                </motion.div>
              ))
            ) : categories.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <Box
                  sx={{
                    ...glass,
                    borderRadius: "24px",
                    textAlign: "center",
                    py: 8,
                    px: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "18px",
                      background: C.indigoSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2.5,
                    }}
                  >
                    <FolderRoundedIcon sx={{ color: C.indigo, fontSize: 30 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "1.2rem",
                      color: C.ink,
                      mb: 0.8,
                    }}
                  >
                    No categories yet
                  </Typography>
                  <Typography
                    sx={{ color: C.slate, fontSize: "0.85rem", mb: 3.5 }}
                  >
                    Create your first category to organise videos
                  </Typography>
                  <Button
                    onClick={handleCreate}
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    sx={{
                      borderRadius: "12px",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      textTransform: "none",
                      background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                      boxShadow: "0 6px 20px rgba(99,102,241,0.32)",
                    }}
                  >
                    Add Category
                  </Button>
                </Box>
              </motion.div>
            ) : (
              categories.map((cat, i) => (
                <CategoryCard
                  key={cat.id}
                  cat={cat}
                  index={i}
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                />
              ))
            )}
          </AnimatePresence>
        </Stack>
      </Box>

      {/* ── CREATE / EDIT DIALOG ── */}
      <PremiumDialog
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Category" : "New Category"}
        actions={
          <>
            <Button
              onClick={() => setOpen(false)}
              disabled={submitting}
              sx={{
                color: C.slate,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!name.trim() || submitting}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "12px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${C.indigo}, ${C.violet})`,
                  boxShadow: "0 6px 20px rgba(99,102,241,0.32)",
                  minWidth: 100,
                }}
              >
                {submitting ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : editingId ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </motion.div>
          </>
        }
      >
        <TextField
          label="Category name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          autoFocus
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              fontFamily: "'DM Sans', sans-serif",
              background: "rgba(255,255,255,0.6)",
            },
            "& label": { fontFamily: "'DM Sans', sans-serif" },
          }}
        />
      </PremiumDialog>

      {/* ── DELETE DIALOG ── */}
      <PremiumDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Category?"
        actions={
          <>
            <Button
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              sx={{
                color: C.slate,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "12px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(135deg,#ef4444,#dc2626)",
                  boxShadow: "0 6px 20px rgba(239,68,68,0.28)",
                  minWidth: 100,
                }}
              >
                {deleting ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  "Delete"
                )}
              </Button>
            </motion.div>
          </>
        }
      >
        <Box
          sx={{
            background: C.redSoft,
            borderRadius: "14px",
            px: 2.5,
            py: 2,
            border: "1px solid rgba(239,68,68,0.14)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: "#b91c1c",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            <strong>"{deleteTarget?.title}"</strong> will be permanently
            deleted. This cannot be undone.
          </Typography>
        </Box>
      </PremiumDialog>
    </Box>
  );
}
